import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { editGridTime, type GridEditMode } from "../lib/grid-edit";
import { Link } from "react-router-dom";
import {
  formatCad,
  INACTIVE_COLOR,
  isOnlineBooking,
  type AppointmentView,
} from "@shared/modules/x-atelier";
import { toDate } from "../../../core/lib/format";
import { dayKey, formatDayLong, formatHour, formatWeekday, isToday } from "../lib/dates";
import { dueCents, isInactive } from "../lib/status";
import { StatusDot } from "./status-badge";
import { BookingChannelBadge } from "./booking-badges";

/**
 * Horaire à l'heure : les heures en colonne à gauche, un rendez-vous occupe la
 * hauteur de sa durée. C'est la lecture que l'atelier connaît — on voit les
 * trous de la journée, pas seulement la liste de ce qui est réservé.
 */

/** Hauteur d'une heure, en pixels. Doit correspondre à `--grid-hour` dans la feuille. */
/** Appui long avant de saisir un bloc au doigt. */
const LONG_PRESS_MS = 380;
const HOUR_PX = 64;
/** Pas de création au clic : la demi-heure. */
const SLOT_MIN = 30;
/** Fenêtre affichée par défaut ; elle s'élargit si un rendez-vous déborde. */
const DEFAULT_FROM = 7;
const DEFAULT_TO = 18;

interface Placed {
  appointment: AppointmentView;
  /** Minutes depuis minuit. */
  startMin: number;
  endMin: number;
  /** Colonne et nombre de colonnes quand des rendez-vous se chevauchent. */
  lane: number;
  lanes: number;
}

function minutesOf(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

/**
 * Place les rendez-vous d'une journée : bornes en minutes et, quand ils se
 * chevauchent, répartition en colonnes côte à côte (un groupe qui se recoupe
 * partage la largeur).
 */
export function placeDay(items: AppointmentView[]): Placed[] {
  const placed: Placed[] = [];
  for (const a of items) {
    const start = toDate(a.startAt);
    const end = toDate(a.endAt);
    if (!start) continue;
    const startMin = minutesOf(start);
    // Un rendez-vous qui finit le lendemain (ou sans fin) s'arrête au bas de la grille.
    const rawEnd =
      end && end.getTime() > start.getTime()
        ? dayKey(end) !== dayKey(start)
          ? 1440
          : minutesOf(end)
        : startMin + 30;
    const endMin = Math.min(24 * 60, rawEnd <= startMin ? 24 * 60 : rawEnd);
    placed.push({ appointment: a, startMin, endMin, lane: 0, lanes: 1 });
  }
  placed.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  // Groupes qui se chevauchent : tant qu'un rendez-vous commence avant la fin
  // du plus tardif du groupe, il en fait partie et prend la première colonne libre.
  let group: Placed[] = [];
  let groupEnd = -1;
  const closeGroup = () => {
    const lanes = group.reduce((max, p) => Math.max(max, p.lane + 1), 1);
    for (const p of group) p.lanes = lanes;
    group = [];
    groupEnd = -1;
  };
  for (const p of placed) {
    if (group.length > 0 && p.startMin >= groupEnd) closeGroup();
    const taken = new Set(group.filter((g) => g.endMin > p.startMin).map((g) => g.lane));
    let lane = 0;
    while (taken.has(lane)) lane++;
    p.lane = lane;
    group.push(p);
    groupEnd = Math.max(groupEnd, p.endMin);
  }
  if (group.length > 0) closeGroup();
  return placed;
}

/** Bornes de la grille : la fenêtre d'atelier, élargie à ce qui déborde. */
export function gridBounds(all: AppointmentView[]): { from: number; to: number } {
  let from = DEFAULT_FROM;
  let to = DEFAULT_TO;
  for (const a of all) {
    const start = toDate(a.startAt);
    const end = toDate(a.endAt);
    if (start) from = Math.min(from, start.getHours());
    if (start && end && dayKey(start) !== dayKey(end)) to = 24;
    else if (end) to = Math.max(to, end.getMinutes() > 0 ? end.getHours() + 1 : end.getHours());
    if (start && !end) to = Math.max(to, start.getHours() + 1);
  }
  return { from: Math.max(0, from), to: Math.min(24, Math.max(to, from + 1)) };
}

interface TimeGridProps {
  /** Une journée (vue Jour) ou les sept (vue Semaine). */
  days: Date[];
  byDay: Map<string, AppointmentView[]>;
  loading?: boolean;
  onReschedule?: (appointment: AppointmentView, start: Date, end: Date) => Promise<void>;
  /** Clic sur un créneau libre : proposer un rendez-vous à cette heure-là. */
  onSlot?: (start: Date) => void;
  /** Clic sur l'en-tête d'une colonne (vue Semaine). */
  onDay?: (d: Date) => void;
  /**
   * Indisponibilités internes d'une journée (pause de midi, fermetures) :
   * bandes hachurées sous les rendez-vous. Un clic reste possible (dérogation).
   */
  blocksFor?: (
    day: Date,
  ) => Array<{ kind: "pause" | "fermeture"; start: number; end: number; label: string }>;
}

export function TimeGrid({
  days,
  byDay,
  loading,
  onSlot,
  onDay,
  onReschedule,
  blocksFor,
}: TimeGridProps) {
  const root = useRef<HTMLDivElement>(null);
  const cleanup = useRef<(() => void) | null>(null);
  const suppressClick = useRef(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<AppointmentView | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  /** Bloc saisi au doigt (appui long), le temps du geste. */
  const [heldId, setHeldId] = useState<string | null>(null);
  useEffect(() => () => cleanup.current?.(), []);

  const beginEdit = (
    event: ReactPointerEvent,
    appointment: AppointmentView,
    mode: GridEditMode,
  ) => {
    if (!onReschedule || loading || saving || event.button !== 0) return;
    const start = toDate(appointment.startAt);
    const end = toDate(appointment.endAt);
    if (!start || !end || end <= start) return;
    event.preventDefault();
    const x = event.clientX;
    const y = event.clientY;
    const pointerId = event.pointerId;
    const body = root.current!.querySelector<HTMLElement>(".time-grid__body")!;
    const scrollTop = body.scrollTop;
    let moved = false;
    let proposal: ReturnType<typeof editGridTime> | null = null;
    // Au doigt : le bloc se saisit après un appui long (le défilement garde
    // le geste court) ; à la souris, tout de suite. Les blocs sont en
    // `touch-action: none` : avant la prise, le défilement se reproduit à la main.
    const touch = event.pointerType === "touch";
    let held = !touch;
    let scrolling = false;
    let lastY = y;
    const holdTimer = touch
      ? window.setTimeout(() => {
          if (scrolling) return;
          held = true;
          suppressClick.current = true;
          navigator.vibrate?.(15);
          setActiveId(appointment.id);
          setHeldId(appointment.id);
        }, LONG_PRESS_MS)
      : null;
    const move = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      if (!held) {
        if (touch) {
          if (Math.hypot(e.clientX - x, e.clientY - y) > 8) scrolling = true;
          if (scrolling) {
            body.scrollTop -= e.clientY - lastY;
            lastY = e.clientY;
          }
        }
        return;
      }
      if (!moved && !touch && Math.hypot(e.clientX - x, e.clientY - y) < 5) return;
      moved = true;
      suppressClick.current = true;
      const bounds = body.getBoundingClientRect();
      if (e.clientY < bounds.top + 28) body.scrollTop -= 12;
      if (e.clientY > bounds.bottom - 28) body.scrollTop += 12;
      const columns = Array.from(body.querySelectorAll<HTMLElement>(".time-grid__column"));
      const index = columns.findIndex((column) => {
        const rect = column.getBoundingClientRect();
        return e.clientX >= rect.left && e.clientX <= rect.right;
      });
      if (index < 0 || e.clientY < bounds.top || e.clientY > bounds.bottom) {
        proposal = null;
        setPreview(null);
        return;
      }
      proposal = editGridTime(
        start,
        end,
        days[index]!,
        ((e.clientY - y + body.scrollTop - scrollTop) * 60) / HOUR_PX,
        mode,
      );
      setActiveId(appointment.id);
      setPreview({
        ...appointment,
        startAt: proposal.start.toISOString(),
        endAt: proposal.end.toISOString(),
      });
    };
    const clear = () => {
      if (holdTimer !== null) window.clearTimeout(holdTimer);
      setHeldId(null);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("keydown", key);
      window.removeEventListener("blur", cancel);
      cleanup.current = null;
    };
    const reset = () => {
      setPreview(null);
      setActiveId(null);
    };
    const cancel = () => {
      clear();
      reset();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      }
    };
    const finish = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      clear();
      if (
        !proposal ||
        !moved ||
        (proposal.start.getTime() === start.getTime() && proposal.end.getTime() === end.getTime())
      ) {
        reset();
        return;
      }
      setSaving(true);
      void onReschedule(appointment, proposal.start, proposal.end).finally(() => {
        setSaving(false);
        reset();
      });
    };
    cleanup.current?.();
    suppressClick.current = false;
    cleanup.current = clear;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", cancel);
    window.addEventListener("keydown", key);
    window.addEventListener("blur", cancel);
  };
  const visible = days.flatMap((d) => byDay.get(dayKey(d)) ?? []);
  const { from, to } = gridBounds(visible);
  const hours = Array.from({ length: to - from }, (_, i) => from + i);
  const style = {
    "--grid-hour": `${HOUR_PX}px`,
    "--grid-days": String(days.length),
  } as CSSProperties;

  return (
    <div
      ref={root}
      className={`time-grid${days.length === 1 ? " time-grid--single" : ""}`}
      style={style}
      onPointerDownCapture={() => {
        suppressClick.current = false;
      }}
      onClickCapture={(e) => {
        if (suppressClick.current) {
          e.preventDefault();
          e.stopPropagation();
          suppressClick.current = false;
        }
      }}
    >
      <span className="time-grid__status" role="status">
        {saving ? "Enregistrement du rendez-vous…" : ""}
      </span>
      <div className="time-grid__head">
        <span className="time-grid__corner" aria-hidden="true" />
        {days.map((d) => {
          const count = (byDay.get(dayKey(d)) ?? []).filter((a) => !isInactive(a.status)).length;
          const classes = `time-grid__day-head${isToday(d) ? " is-today" : ""}`;
          return onDay ? (
            <button
              key={dayKey(d)}
              type="button"
              className={classes}
              onClick={() => onDay(d)}
              title="Ouvrir la journée"
            >
              <span className="time-grid__weekday">{formatWeekday(d)}</span>
              <span className="time-grid__date">{d.getDate()}</span>
              {count > 0 ? <span className="time-grid__count">{count}</span> : null}
            </button>
          ) : (
            <span key={dayKey(d)} className={classes}>
              <span className="time-grid__weekday">{formatWeekday(d)}</span>
              <span className="time-grid__date">{d.getDate()}</span>
            </span>
          );
        })}
      </div>

      <div className="time-grid__body">
        <div className="time-grid__hours" aria-hidden="true">
          {hours.map((h) => (
            <span key={h} className="time-grid__hour">
              <span className="time-grid__hour-label">{h} h</span>
            </span>
          ))}
        </div>

        {days.map((d) => {
          const items = (byDay.get(dayKey(d)) ?? []).filter((a) => !a.schedulePending);
          return (
            <div
              key={dayKey(d)}
              className={`time-grid__column${isToday(d) ? " is-today" : ""}`}
              aria-label={formatDayLong(d)}
            >
              {/* Les cases de l'horaire : cliquer une case libre ouvre un rendez-vous à cette heure. */}
              {hours.flatMap((h) =>
                Array.from({ length: 60 / SLOT_MIN }, (_, i) => {
                  const minute = i * SLOT_MIN;
                  const at = new Date(d);
                  at.setHours(h, minute, 0, 0);
                  return (
                    <button
                      key={`${h}:${minute}`}
                      type="button"
                      className={`time-grid__slot${minute === 0 ? " is-hour" : ""}`}
                      style={{ top: `${((h - from) * 60 + minute) * (HOUR_PX / 60)}px` }}
                      onClick={onSlot ? () => onSlot(at) : undefined}
                      disabled={!onSlot}
                      aria-label={`${formatDayLong(d)} à ${h} h ${String(minute).padStart(2, "0")} — nouveau rendez-vous`}
                    />
                  );
                }),
              )}

              {(blocksFor?.(d) ?? []).map((b) => {
                const start = new Date(b.start);
                const end = new Date(b.end);
                const startMin = Math.max(
                  from * 60,
                  dayKey(start) === dayKey(d) ? start.getHours() * 60 + start.getMinutes() : 0,
                );
                const endMin =
                  dayKey(end) === dayKey(d) ? end.getHours() * 60 + end.getMinutes() : 24 * 60;
                if (endMin <= startMin) return null;
                return (
                  <span
                    key={`${b.kind}-${b.start}`}
                    className={`time-grid__block time-grid__block--${b.kind}`}
                    style={{
                      top: `${(startMin - from * 60) * (HOUR_PX / 60)}px`,
                      height: `${(endMin - startMin) * (HOUR_PX / 60)}px`,
                    }}
                    title={`${b.label} — indisponible pour les réservations du site et de Léo`}
                    aria-hidden="true"
                  >
                    {b.label}
                  </span>
                );
              })}

              {placeDay(items).map((p) => (
                <GridBlock
                  key={p.appointment.id}
                  placed={p}
                  from={from}
                  dense={days.length > 1}
                  editing={activeId === p.appointment.id}
                  held={heldId === p.appointment.id}
                  onEdit={onReschedule && !saving && !loading ? beginEdit : undefined}
                />
              ))}

              {preview && dayKey(toDate(preview.startAt)!) === dayKey(d) ? (
                <GridBlock placed={placeDay([preview])[0]!} from={from} preview />
              ) : null}
              {loading && items.length === 0 ? (
                <span className="time-grid__loading" aria-hidden="true" />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Un rendez-vous dans l'horaire : haut et hauteur d'après ses minutes. */
function GridBlock({
  placed,
  from,
  dense,
  editing,
  held,
  preview,
  onEdit,
}: {
  placed: Placed;
  from: number;
  /** Vue Semaine : sept colonnes, donc deux rendez-vous simultanés suffisent à tout serrer. */
  dense?: boolean;
  editing?: boolean;
  /** Saisi au doigt (appui long) : prêt à glisser. */
  held?: boolean;
  preview?: boolean;
  onEdit?: (event: ReactPointerEvent, appointment: AppointmentView, mode: GridEditMode) => void;
}) {
  const { appointment: a, startMin, endMin, lane, lanes } = placed;
  const top = (startMin - from * 60) * (HOUR_PX / 60);
  const height = Math.max(22, (endMin - startMin) * (HOUR_PX / 60));
  const minutes = endMin - startMin;
  const due = dueCents(a);
  // Annulé ou non présenté : rouge, quelle que soit la catégorie — c'est ce
  // qu'on cherche des yeux dans la journée, pas le service qui était prévu.
  const inactive = isInactive(a.status);
  const style = {
    top: `${top}px`,
    height: `${height}px`,
    left: `calc(${(lane / lanes) * 100}% + 2px)`,
    width: `calc(${100 / lanes}% - 4px)`,
    "--rdv-color": inactive ? INACTIVE_COLOR : (a.colorHex ?? a.technicianColor ?? undefined),
  } as CSSProperties;
  const classes = [
    "time-grid__event",
    onEdit && "time-grid__event--editable",
    editing && "time-grid__event--editing",
    held && "time-grid__event--held",
    preview && "time-grid__event--preview",
    // Ce qu'on montre dépend de la place réellement disponible : la hauteur
    // vient de la durée, la largeur du nombre de rendez-vous qui se chevauchent.
    minutes < 90 && "time-grid__event--short",
    minutes < 40 && "time-grid__event--tiny",
    (lanes >= 3 || (dense && lanes >= 2)) && "time-grid__event--narrow",
    inactive && "time-grid__event--inactive",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Link
      to={`/travaux/${a.id}`}
      draggable={false}
      tabIndex={preview ? -1 : undefined}
      aria-hidden={preview || undefined}
      onPointerDown={
        onEdit
          ? (event) => {
              const edge = (event.target as HTMLElement).closest<HTMLElement>("[data-resize]")
                ?.dataset.resize;
              onEdit(event, a, edge === "start" || edge === "end" ? edge : "move");
            }
          : undefined
      }
      className={classes}
      style={style}
      title={`${formatHour(a.startAt)} – ${formatHour(a.endAt)} · ${a.title} — ${a.clientName}${
        a.categoryName ? ` · ${a.categoryName}` : ""
      }${isOnlineBooking(a.bookingChannel) ? " · Réservé en ligne" : ""}${
        a.presenceConfirmedAt ? " · Présence confirmée" : ""
      }`}
    >
      {onEdit ? (
        <span
          className="time-grid__resize time-grid__resize--start"
          data-resize="start"
          title="Glisser pour modifier le début"
        />
      ) : null}
      {onEdit ? (
        <span
          className="time-grid__resize time-grid__resize--end"
          data-resize="end"
          title="Glisser pour modifier la fin"
        />
      ) : null}
      {/* L'heure et l'état sur la même ligne : dans un bloc court, c'est la
          seule qui tient à coup sûr. */}
      <span className="time-grid__event-head">
        <span className="time-grid__event-time">
          {formatHour(a.startAt)}
          {preview ? ` – ${formatHour(a.endAt)}` : ""}
        </span>
        <StatusDot status={a.status} />
        <BookingChannelBadge channel={a.bookingChannel} compact />
      </span>
      <span className="time-grid__event-title">{a.title}</span>
      {/* Le client n'apparaît qu'à partir d'une heure : en dessous, la ligne
          serait coupée par le bas du bloc. */}
      {minutes >= 60 ? <span className="time-grid__event-client">{a.clientName}</span> : null}
      {minutes >= 90 ? (
        <span className="time-grid__event-foot">
          {a.technicianName ? (
            <span className="time-grid__event-tech">{a.technicianName.split(/[\s-]/)[0]}</span>
          ) : null}
          <span className="time-grid__event-due">
            {due === 0 && a.totalWithTaxCents > 0 ? "Payé" : formatCad(due)}
          </span>
        </span>
      ) : null}
    </Link>
  );
}
